import { type FC, useCallback } from 'react';

import { SearchFromPageType } from '../../base/models/SearchFromPageType';
import { useAppSelector } from '../../common/hooks/useThunkDispatch';
import { storeSelectors } from '../../common/selectors/storeSelectors';
import { SearchStateProvider } from '../../search/contexts/searchStateContext';
import searchFlow from '../../search/flow/searchFlow';
import { useSearchAbTestContext } from './searchAbTestContext';

interface ProviderProps {
    initialQuery?: string;
}

const searchProductsAbortable = async ({
    abortSignal,
    query,
    storeId,
    isDirect,
    isAbTest,
}: {
    abortSignal: AbortSignal;
    query: string;
    storeId: string;
    isDirect: boolean;
    isAbTest: boolean;
}) => {
    // fake emtpy result if we are in the autocomplete abtest
    const searchPromise = searchFlow.searchProductsLoop({
        searchRequest: {
            page: 0,
            resultsPerPage: 15,
            searchTerm: query,
            facets: [],
            sortBy: [],
            storeId,
        },
        abortSignal,
        searchService: null,
        quickSearch: isDirect,
        abTest: isAbTest,
    });

    return Promise.all([
        searchPromise,
        searchFlow.autocomplete({
            searchTerm: query,
            skip: 0,
            take: 6,
            abortSignal,
            searchService: null,
            storeId,
        }),
    ]);
};

const EcommerceProductSearchStateContextProvider: FC<React.PropsWithChildren<ProviderProps>> = (
    props,
) => {
    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);
    const { isVariant } = useSearchAbTestContext();

    const searchProductsFunction = useCallback(
        async (_abortSignal: AbortSignal, _query: string, _searchType: 'default' | 'direct') =>
            searchProductsAbortable({
                abortSignal: _abortSignal,
                query: _query,
                storeId: currentProductionUnit,
                isDirect: _searchType === 'direct',
                isAbTest: isVariant,
            }),
        [currentProductionUnit, isVariant],
    );

    return (
        <SearchStateProvider
            initialQuery={props.initialQuery || null}
            searchFunction={searchProductsFunction}
            searchFromPage={SearchFromPageType.Ecommerce}
            minimumQueryLength={3}
            initialSearchType="direct"
        >
            {props.children}
        </SearchStateProvider>
    );
};

export default EcommerceProductSearchStateContextProvider;
